<template>
    <div class="reservations-view d-flex flex-column h-100 overflow-hidden">
        <div class="flex-shrink-0">
            <div class="d-flex align-center pb-4">
                <h2 class="text-h5 font-weight-bold">
                    {{ entryType === 'ware' ? $t('sidebar.wareReservations') : $t('sidebar.truckReservations') }}
                </h2>
                <v-btn
                    icon
                    size="large"
                    variant="text"
                    elevation="0"
                    @click="infoModalVisible = true"
                >
                    <v-icon size="large">mdi-information-outline</v-icon>
                    <v-tooltip activator="parent" location="bottom">
                        {{ $t('reservations.processSteps.title') }}
                    </v-tooltip>
                </v-btn>
            </div>
        </div>
        <div class="reservations-tabs-wrapper d-flex flex-column flex-grow-1 overflow-x-hidden min-h-0">
            <div class="d-flex align-center ga-4 flex-shrink-0">
                <v-tabs v-model="activeTab">
                    <v-tab value="contracted" elevation="0" class="d-flex align-center">
                        {{ $t('reservations.contracted') }}
                        <ReservationBadge :count="contractedBadgeCount" />
                    </v-tab>
                    <v-tab value="received" elevation="0" class="d-flex align-center">
                        {{ $t('reservations.received') }}
                        <ReservationBadge :count="receivedBadgeCount" />
                    </v-tab>
                </v-tabs>
            </div>

            <div class="reservations-filterbar-wrap px-2 pb-2 pt-2 flex-shrink-0">
                <EntriesFilterBar
                    v-if="activeTab === 'contracted'"
                    v-model="contractedFilters"
                    v-model:expanded="contractedFilterExpanded"
                    :title="$t('reservation.filterContracted')"
                    variant="reservations"
                    :show-sort-toggle="false"
                    :reservation-status-options="reservationStatusOptions"
                    :results-count="filteredContractedReservations.length"
                />
                <EntriesFilterBar
                    v-else
                    v-model="receivedFilters"
                    v-model:expanded="receivedFilterExpanded"
                    :title="$t('reservation.filterReceived')"
                    variant="reservations"
                    :show-sort-toggle="false"
                    :reservation-status-options="reservationStatusOptions"
                    :results-count="filteredReceivedReservations.length"
                />
            </div>

            <div class="reservations-list-scroll flex-grow-1 overflow-y-auto overflow-x-hidden">
                <v-tabs-window v-model="activeTab">
                    <v-tabs-window-item value="contracted">
                        <div class="mt-2 px-2 pb-3 pt-2">
                            <ReservationList
                                :reservations="paginatedContractedReservations"
                                :entry-type="entryType"
                                :is-entry-owner="false"
                                :loading="loading"
                                :chat-unread-by-reservation="chatUnreadByReservation"
                                @cancel="openCancelConfirm"
                                @accept-price-counter-offer="handleAcceptPriceCounterOffer"
                                @price-offer="handlePriceOffer"
                                @confirm-completed="handleConfirmCompleted"
                                @rated="handleRated"
                                @generate-transport-order="handleGenerateTransportOrder"
                                @download-transport-order="handleDownloadTransportOrder"
                                @modify-transport-order="handleModifyTransportOrder"
                                @send-transport-order="handleSendTransportOrder"
                                @upload-signed-pdf="handleUploadSignedPdf"
                                @upload-cmr="handleUploadCmr"
                                @upload-invoice="handleUploadInvoice"
                                @upload-payment-proof="handleUploadPaymentProof"
                                @upload-generated-transport-order="handleUploadGeneratedTransportOrder"
                                @confirm-transport-order="handleConfirmTransportOrder"
                                @edit-reservation-schedule="(reservation) => openReservationScheduleEdit(reservation, false)"
                                @chat-read="onChatRead"
                            />
                        </div>
                    </v-tabs-window-item>
                    <v-tabs-window-item value="received">
                        <div class="px-2 pb-3 pt-2">
                            <ReservationList
                                :reservations="paginatedReceivedReservations"
                                :entry-type="entryType"
                                :is-entry-owner="true"
                                :loading="loading"
                                :chat-unread-by-reservation="chatUnreadByReservation"
                                @confirm="handleConfirm"
                                @price-counter-offer="handlePriceCounterOffer"
                                @decline="handleDecline"
                                @cancel="openCancelConfirm"
                                @confirm-completed="handleConfirmCompleted"
                                @rated="handleRated"
                                @generate-transport-order="handleGenerateTransportOrder"
                                @download-transport-order="handleDownloadTransportOrder"
                                @modify-transport-order="handleModifyTransportOrder"
                                @send-transport-order="handleSendTransportOrder"
                                @upload-signed-pdf="handleUploadSignedPdf"
                                @upload-cmr="handleUploadCmr"
                                @upload-invoice="handleUploadInvoice"
                                @upload-payment-proof="handleUploadPaymentProof"
                                @upload-generated-transport-order="handleUploadGeneratedTransportOrder"
                                @confirm-transport-order="handleConfirmTransportOrder"
                                @edit-reservation-schedule="(reservation) => openReservationScheduleEdit(reservation, true)"
                                @chat-read="onChatRead"
                            />
                        </div>
                    </v-tabs-window-item>
                </v-tabs-window>
            </div>

            <ListPagination
                class="reservations-pagination px-2 pt-3 pb-3"
                clear-app-fab
                :page="activeListPage"
                :items-per-page="pageSize"
                :total-items="activeFilteredCount"
                :items-per-page-options="pageSizeOptions"
                @update:page="setActiveListPage"
                @update:items-per-page="setPageSize"
            />
        </div>

            <TransportOrderModal
                v-if="transportOrderReservation"
                v-model:visible="transportOrderModalVisible"
                :reservation="transportOrderReservation"
                :entry-type="entryType"
                :field-values="transportOrderFieldValues"
                @save="handleTransportOrderSave"
            />

            <ReservationScheduleEditModal
                v-if="scheduleEditReservation"
                v-model:visible="scheduleEditModalVisible"
                :reservation="scheduleEditReservation"
                :entry-type="entryType"
                :is-entry-owner="scheduleEditIsEntryOwner"
                @saved="handleReservationScheduleSaved"
                @error="handleReservationScheduleError"
            />

            <ConfirmDialog
                :visible="showCancelConfirmDialog"
                :title="$t('reservations.confirmCancelTitle')"
                :message="$t('reservations.confirmCancelMessage', { id: cancelReservationToConfirm?.entry_summary?.identification_id || cancelReservationToConfirm?.uid || '' })"
                :primary-text="$t('common.yes')"
                secondary-color="error"
                :secondary-text="$t('common.no')"
                @update:visible="(isVisible) => { if (!isVisible) closeCancelConfirm(); }"
                @primary="confirmCancelReservation"
                @secondary="closeCancelConfirm"
            />

            <v-dialog v-model="infoModalVisible" max-width="600" persistent scrim="black">
                <v-card>
                    <v-card-title class="text-center justify-center">
                        {{ $t("reservations.processFlow.title") }}
                    </v-card-title>
                    <v-card-text class="reservation-info-content">
                        <!-- Roles stay visible; numbered steps use expansion panels (collapsed until opened). -->
                        <v-list density="compact" class="py-0">
                            <v-list-subheader>{{ $t(processFlowRolesSection.titleKey) }}</v-list-subheader>
                            <v-list-item
                                v-for="itemKey in processFlowRolesSection.itemKeys"
                                :key="itemKey"
                                class="px-0"
                            >
                                <v-list-item-title class="text-body-2 text-wrap">
                                    {{ $t(itemKey) }}
                                </v-list-item-title>
                            </v-list-item>
                        </v-list>
                        <v-expansion-panels
                            v-model="processFlowOpenPanels"
                            class="reservation-process-flow-panels"
                            elevation="0"
                            multiple
                        >
                            <v-expansion-panel
                                v-for="(section, stepIndex) in processFlowStepSections"
                                :key="section.titleKey"
                                class="reservation-process-flow-panel"
                            >
                                <v-expansion-panel-title class="reservation-process-flow-panel-title text-body-2">
                                    {{ $t(section.titleKey) }}
                                </v-expansion-panel-title>
                                <v-expansion-panel-text class="pa-0">
                                    <v-list density="compact" class="py-0">
                                        <template
                                            v-for="(itemKey, itemIndex) in section.itemKeys"
                                            :key="itemKey"
                                        >
                                            <v-list-item class="px-0">
                                                <v-list-item-title
                                                    v-if="itemKey === 'reservations.processFlow.sections.step8.line3'"
                                                    class="text-body-2 text-wrap"
                                                >
                                                    {{ $t("reservations.processFlow.sections.step8.line3Part1") }}<strong>{{ $t("reservations.processFlow.sections.step8.line3Bold") }}</strong>{{ $t("reservations.processFlow.sections.step8.line3Part2") }}
                                                </v-list-item-title>
                                                <v-list-item-title v-else class="text-body-2 text-wrap">
                                                    {{ $t(itemKey) }}
                                                </v-list-item-title>
                                            </v-list-item>
                                            <div
                                                v-if="stepIndex === 3 && itemIndex === 3"
                                                class="reservation-process-video-wrap mt-3"
                                            >
                                                <video
                                                    class="reservation-process-video"
                                                    controls
                                                    playsinline
                                                    preload="metadata"
                                                    :aria-label="$t('reservations.processFlow.videoAriaLabel')"
                                                >
                                                    <source :src="`${$baseUrl}date_modifications_text.mp4`" type="video/mp4" />
                                                </video>
                                            </div>
                                        </template>
                                    </v-list>
                                    <div
                                        v-if="stepIndex === 0"
                                        class="reservation-process-video-wrap mt-3"
                                    >
                                        <video
                                            class="reservation-process-video"
                                            controls
                                            playsinline
                                            preload="metadata"
                                            :aria-label="$t('reservations.processFlow.videoAriaLabel')"
                                        >
                                            <source
                                                :src="`${$baseUrl}${entryType === 'ware' ? 'ware_text.mp4' : 'trucks_text.mp4'}`"
                                                type="video/mp4"
                                            />
                                        </video>
                                    </div>
                                    <div
                                        v-if="stepIndex === 2"
                                        class="reservation-process-video-wrap mt-3"
                                    >
                                        <video
                                            class="reservation-process-video"
                                            controls
                                            playsinline
                                            preload="metadata"
                                            :aria-label="$t('reservations.processFlow.videoAriaLabel')"
                                        >
                                            <source :src="`${$baseUrl}offer_counter_offer_text.mp4`" type="video/mp4" />
                                        </video>
                                    </div>
                                </v-expansion-panel-text>
                            </v-expansion-panel>
                        </v-expansion-panels>
                    </v-card-text>
                    <v-card-actions class="justify-center">
                        <v-btn variant="flat" @click="infoModalVisible = false">{{ $t('common.close') }}</v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>
    </div>
</template>

<script>
import axios from "axios";
import emitter from "@/eventBus";
import { getChatUnreadCount, markReservationTabViewed } from "@/api";
import ConfirmDialog from "@/components/display/ConfirmDialog.vue";
import ListPagination from "@/components/display/ListPagination.vue";
import ReservationBadge from "@/components/ReservationBadge.vue";
import ReservationList from "@/components/reservation/ReservationList.vue";
import EntriesFilterBar from "@/components/display/EntriesFilterBar.vue";
import ReservationScheduleEditModal from "@/components/reservation/ReservationScheduleEditModal.vue";
import TransportOrderModal from "@/components/transport-order/TransportOrderModal.vue";
import { showAlertModal } from "@/utils";

const PAGE_SIZE_OPTIONS = [10, 20, 50];

export default {
    components: {
        ConfirmDialog,
        ListPagination,
        ReservationBadge,
        ReservationList,
        EntriesFilterBar,
        TransportOrderModal,
        ReservationScheduleEditModal,
    },
    props: {
        entryType: {
            type: String,
            required: true,
            validator: (value) => ["ware", "truck"].includes(value),
        },
    },
    data() {
        return {
            infoModalVisible: false,
            /** Indices of open step panels (empty = all collapsed when modal opens). */
            processFlowOpenPanels: [],
            activeTab: "contracted",
            loading: false,
            contractedReservations: [],
            receivedReservations: [],
            contractedFilters: {
                loadingLocation: { country_code: null, place_id: null, place_display_name: null },
                unloadingLocation: { country_code: null, place_id: null, place_display_name: null },
                identificationId: null,
                transportOrderNumber: null,
                statuses: null,
                missingDocuments: false,
                inNegotiation: false,
            },
            receivedFilters: {
                loadingLocation: { country_code: null, place_id: null, place_display_name: null },
                unloadingLocation: { country_code: null, place_id: null, place_display_name: null },
                identificationId: null,
                transportOrderNumber: null,
                statuses: null,
                missingDocuments: false,
                inNegotiation: false,
            },
            contractedFilterExpanded: false,
            receivedFilterExpanded: false,
            contractedPage: 1,
            receivedPage: 1,
            pageSize: 20,
            pageSizeOptions: PAGE_SIZE_OPTIONS,
            transportOrderModalVisible: false,
            transportOrderReservation: null,
            transportOrderFieldValues: null,
            scheduleEditReservation: null,
            scheduleEditModalVisible: false,
            scheduleEditIsEntryOwner: false,
            cancelReservationToConfirm: null,
            chatUnreadByReservation: {},
        };
    },
    watch: {
        activeTab() {
            this.fetchReservations();
        },
        infoModalVisible(isOpen) {
            if (isOpen) this.processFlowOpenPanels = [];
        },
        "$route.query.tab"(tab) {
            if (tab === "received" || tab === "contracted") {
                if (this.activeTab !== tab) {
                    this.activeTab = tab;
                }
            }
        },
        contractedFilters: {
            deep: true,
            handler() {
                this.contractedPage = 1;
            },
        },
        receivedFilters: {
            deep: true,
            handler() {
                this.receivedPage = 1;
            },
        },
        filteredContractedReservations(list) {
            const maxPage = Math.max(1, Math.ceil(list.length / this.pageSize) || 1);
            if (this.contractedPage > maxPage) this.contractedPage = maxPage;
        },
        filteredReceivedReservations(list) {
            const maxPage = Math.max(1, Math.ceil(list.length / this.pageSize) || 1);
            if (this.receivedPage > maxPage) this.receivedPage = maxPage;
        },
    },
    computed: {
        processFlowSections() {
            const isWare = this.entryType === "ware";
            return [
                {
                    titleKey: "reservations.processFlow.sections.roles.title",
                    itemKeys: [
                        "reservations.processFlow.sections.roles.line1",
                        "reservations.processFlow.sections.roles.line2",
                    ],
                },
                {
                    titleKey: "reservations.processFlow.sections.step1.title",
                    itemKeys: [
                        "reservations.processFlow.sections.step1.line1",
                        isWare
                            ? "reservations.processFlow.sections.step1.wareLine"
                            : "reservations.processFlow.sections.step1.truckLine",
                        "reservations.processFlow.sections.step1.line2",
                    ],
                },
                {
                    titleKey: "reservations.processFlow.sections.step2.title",
                    itemKeys: [
                        "reservations.processFlow.sections.step2.line1",
                        "reservations.processFlow.sections.step2.line2",
                        "reservations.processFlow.sections.step2.truckLine",
                    ],
                },
                {
                    titleKey: "reservations.processFlow.sections.step3.title",
                    itemKeys: [
                        "reservations.processFlow.sections.step3.line1",
                        "reservations.processFlow.sections.step3.line2",
                        "reservations.processFlow.sections.step3.line3",
                        "reservations.processFlow.sections.step3.line4",
                    ],
                },
                {
                    titleKey: "reservations.processFlow.sections.step4.title",
                    itemKeys: [
                        "reservations.processFlow.sections.step4.line1",
                        "reservations.processFlow.sections.step4.line2",
                        "reservations.processFlow.sections.step4.line3",
                        "reservations.processFlow.sections.step4.line4",
                        "reservations.processFlow.sections.step4.line5",
                    ],
                },
                {
                    titleKey: "reservations.processFlow.sections.step5.title",
                    itemKeys: [
                        isWare
                            ? "reservations.processFlow.sections.step5.wareLine"
                            : "reservations.processFlow.sections.step5.truckLine",
                        isWare
                            ? "reservations.processFlow.sections.step5.wareConfirmLine"
                            : "reservations.processFlow.sections.step5.truckConfirmLine",
                        "reservations.processFlow.sections.step5.line2",
                    ],
                },
                {
                    titleKey: "reservations.processFlow.sections.step6.title",
                    itemKeys: [
                        "reservations.processFlow.sections.step6.line1",
                        "reservations.processFlow.sections.step6.line2",
                    ],
                },
                {
                    titleKey: "reservations.processFlow.sections.step7.title",
                    itemKeys: [
                        "reservations.processFlow.sections.step7.line1",
                        "reservations.processFlow.sections.step7.line2",
                    ],
                },
                {
                    titleKey: "reservations.processFlow.sections.step8.title",
                    itemKeys: [
                        "reservations.processFlow.sections.step8.line1",
                        "reservations.processFlow.sections.step8.line2",
                        "reservations.processFlow.sections.step8.line3",
                    ],
                },
                {
                    titleKey: "reservations.processFlow.sections.step9.title",
                    itemKeys: [
                        "reservations.processFlow.sections.step9.line1",
                        isWare
                            ? "reservations.processFlow.sections.step9.wareLine"
                            : "reservations.processFlow.sections.step9.truckLine",
                        "reservations.processFlow.sections.step9.line2",
                        "reservations.processFlow.sections.step9.paymentProofLine",
                    ],
                },
                {
                    titleKey: "reservations.processFlow.sections.step10.title",
                    itemKeys: [
                        "reservations.processFlow.sections.step10.line1",
                        "reservations.processFlow.sections.step10.line2",
                    ],
                },
            ];
        },
        processFlowRolesSection() {
            return this.processFlowSections[0];
        },
        processFlowStepSections() {
            return this.processFlowSections.slice(1);
        },
        receivedBadgeCount() {
            const pending = (this.receivedReservations || []).filter((r) => r.status === "pending").length;
            return pending + this.chatUnreadSumFor(this.receivedReservations);
        },
        contractedBadgeCount() {
            const waitingCounterOffers = (this.contractedReservations || []).filter((reservation) =>
                this.counterOfferIsWaitingOnReserver(reservation)
            ).length;
            return waitingCounterOffers + this.chatUnreadSumFor(this.contractedReservations);
        },
        reservationStatusOptions() {
            const values = [
                "pending",
                "waiting_for_to",
                "waiting_for_signed_to",
                "waiting_for_confirmation",
                "confirmed",
                "in_progress",
                "completed_and_awaiting_confirmation",
                "completed_and_confirmed",
                "declined",
            ];
            return values.map((value) => ({ value, label: this.$t(`reservationStatus.${value}`) }));
        },
        filteredContractedReservations() {
            return this.filterReservations(this.contractedReservations || [], this.contractedFilters);
        },
        filteredReceivedReservations() {
            return this.filterReservations(this.receivedReservations || [], this.receivedFilters);
        },
        paginatedContractedReservations() {
            const start = (this.contractedPage - 1) * this.pageSize;
            return this.filteredContractedReservations.slice(start, start + this.pageSize);
        },
        paginatedReceivedReservations() {
            const start = (this.receivedPage - 1) * this.pageSize;
            return this.filteredReceivedReservations.slice(start, start + this.pageSize);
        },
        activeFilteredCount() {
            return this.activeTab === "received"
                ? this.filteredReceivedReservations.length
                : this.filteredContractedReservations.length;
        },
        activeListPage() {
            return this.activeTab === "received" ? this.receivedPage : this.contractedPage;
        },
        showCancelConfirmDialog: {
            get() {
                return this.cancelReservationToConfirm !== null;
            },
            set(isVisible) {
                if (!isVisible) this.cancelReservationToConfirm = null;
            },
        },
    },
    async mounted() {
        const raw = this.$route.query.reservation;
        const tabQ = this.$route.query.tab;
        let tabChanged = false;
        if (
            (raw == null || String(raw).trim() === "") &&
            (tabQ === "received" || tabQ === "contracted")
        ) {
            if (this.activeTab !== tabQ) {
                this.activeTab = tabQ;
                tabChanged = true;
            }
        }
        if (raw != null && String(raw).trim() !== "") {
            await this.applyReservationDeepLink(String(raw).trim());
        } else if (!tabChanged) {
            this.fetchReservations();
        }
        emitter.on("ws-update", this.handleReservationWsUpdate);
        emitter.on("refresh-chat-unread", this.fetchChatUnread);
        this.fetchChatUnread();
    },
    beforeUnmount() {
        emitter.off("ws-update", this.handleReservationWsUpdate);
        emitter.off("refresh-chat-unread", this.fetchChatUnread);
    },
    methods: {
        counterOfferIsWaitingOnReserver(reservation) {
            if (!reservation || reservation.status !== "pending") return false;
            if (reservation.price_counter_offer == null) return false;
            if (reservation.price_counter_offer_accepted_at) return false;
            const offerSent = reservation.price_offer_sent_at;
            const counterSent = reservation.price_counter_offer_sent_at;
            if (!offerSent || !counterSent) return false;
            return counterSent > offerSent;
        },
        chatUnreadSumFor(list) {
            const map = this.chatUnreadByReservation || {};
            return (list || []).reduce((sum, r) => {
                if (!r?.uid) return sum;
                const n = map[r.uid] ?? map[String(r.uid)] ?? 0;
                return sum + (Number(n) || 0);
            }, 0);
        },
        async fetchChatUnread() {
            try {
                const data = await getChatUnreadCount();
                this.chatUnreadByReservation = data.by_reservation || {};
            } catch {
                this.chatUnreadByReservation = {};
            }
        },
        onChatRead(reservationUid) {
            if (!reservationUid) return;
            const next = { ...this.chatUnreadByReservation };
            delete next[reservationUid];
            this.chatUnreadByReservation = next;
            emitter.emit("refresh-chat-unread");
        },
        setActiveListPage(page) {
            if (this.activeTab === "received") {
                this.receivedPage = page;
            } else {
                this.contractedPage = page;
            }
        },
        setPageSize(pageSize) {
            this.pageSize = pageSize;
            this.contractedPage = 1;
            this.receivedPage = 1;
        },
        filterReservations(list, filters) {
            const loadingLocation = filters?.loadingLocation || {};
            const unloadingLocation = filters?.unloadingLocation || {};
            const identificationIdNeedle = (filters?.identificationId || "").trim().toUpperCase();
            const toNeedle = (filters?.transportOrderNumber || "").trim().toUpperCase();
            const selectedStatuses = filters?.statuses;
            const missingDocuments = filters?.missingDocuments || false;
            const inNegotiation = filters?.inNegotiation || false;
            const loadCountry = loadingLocation.country_code || null;
            const loadPlace = loadingLocation.place_id || null;
            const unloadCountry = unloadingLocation.country_code || null;
            const unloadPlace = unloadingLocation.place_id || null;

            return (list || []).filter((reservation) => {
                // statuses === null => "all statuses selected" (no filtering)
                // statuses === []   => none selected (show nothing)
                // statuses === [...] => show only selected
                if (Array.isArray(selectedStatuses)) {
                    if (!selectedStatuses.length) return false;
                    if (!selectedStatuses.includes(reservation?.status)) return false;
                }
                const entrySummary = reservation?.entry_summary || {};
                const load = entrySummary.loading_location || {};
                const unload = entrySummary.unloading_location || {};

                if (identificationIdNeedle) {
                    const hay = String(entrySummary.identification_id || "").toUpperCase();
                    if (!hay.includes(identificationIdNeedle)) return false;
                }
                if (toNeedle) {
                    const hay = String(reservation?.transport_order_preview?.transport_order_number || "").toUpperCase();
                    if (!hay.includes(toNeedle)) return false;
                }

                if (loadCountry && String(load.country_code || "") !== String(loadCountry)) return false;
                if (loadPlace && String(load.place_id || "") !== String(loadPlace)) return false;
                if (unloadCountry && String(unload.country_code || "") !== String(unloadCountry)) return false;
                if (unloadPlace && String(unload.place_id || "") !== String(unloadPlace)) return false;

                // Missing documents filter:show reservations missing ANY of the 4 document types
                if (missingDocuments) {
                    const hasTransportOrder = reservation.has_transport_order || false;
                    const hasSignedPdf = reservation.transport_order_preview?.has_signed_pdf || false;
                    const hasCmr = reservation.transport_order_preview?.has_cmr_pdf || false;
                    const hasInvoice = reservation.transport_order_preview?.has_invoice_pdf || false;
                    const hasPaymentProof = reservation.transport_order_preview?.has_payment_proof || false;
                    if (hasTransportOrder && hasSignedPdf && hasCmr && hasInvoice && hasPaymentProof) return false;
                }

                if (inNegotiation) {
                    if (reservation?.status !== "pending") return false;
                    const hasOffer = reservation.price_offer != null;
                    const hasCounter = reservation.price_counter_offer != null;
                    if (!hasOffer && !hasCounter) return false;
                }

                return true;
            });
        },
        blankReservationFilters(identificationId = null) {
            return {
                loadingLocation: { country_code: null, place_id: null, place_display_name: null },
                unloadingLocation: { country_code: null, place_id: null, place_display_name: null },
                identificationId,
                transportOrderNumber: null,
                statuses: null,
                missingDocuments: false,
                inNegotiation: false,
            };
        },
        async applyReservationDeepLink(uid) {
            try {
                const { data } = await axios.get(
                    `${process.env.VUE_APP_API_URL}/reservations/${encodeURIComponent(uid)}/`
                );
                const formType = data.entry_summary?.form_type;
                if (formType === "ware" || formType === "truck") {
                    if (formType !== this.entryType) {
                        await this.$router.replace({
                            name: formType === "ware" ? "WareReservations" : "TruckReservations",
                            query: { ...this.$route.query, reservation: uid },
                        });
                        return;
                    }
                }
                const companyId = this.$store.state.auth.user?.company;
                const reservedById = data.reserved_by_company?.id;
                const nextTab =
                    companyId != null &&
                    reservedById != null &&
                    String(reservedById) === String(companyId)
                        ? "contracted"
                        : "received";
                const identificationId = data.entry_summary?.identification_id || null;
                const filters = this.blankReservationFilters(identificationId);
                if (nextTab === "contracted") this.contractedFilters = filters;
                else this.receivedFilters = filters;
                if (this.activeTab !== nextTab) this.activeTab = nextTab;
                await this.fetchReservations();
                await this.revealLinkedReservation(uid);
            } catch (error) {
                console.error("Reservation deep link failed:", error);
                await this.fetchReservations();
            }
        },
        handleReservationWsUpdate(data) {
            if (data.type === "chat_unread_affected") {
                this.fetchChatUnread();
                return;
            }
            if (data.type !== "reservation_update") return;
            const formType = data.reservation?.entry_summary?.form_type;
            const shouldRefreshList = !formType || formType === this.entryType;
            if (shouldRefreshList) {
                this.fetchReservations();
            }
            if (data.badge_affected) {
                emitter.emit("refresh-reservation-badge");
            }
        },
        openReservationScheduleEdit(reservation, isEntryOwnerFlag) {
            this.scheduleEditReservation = reservation;
            this.scheduleEditIsEntryOwner = Boolean(isEntryOwnerFlag);
            this.scheduleEditModalVisible = true;
        },
        async handleReservationScheduleSaved() {
            await this.fetchReservations();
            emitter.emit("refresh-reservation-badge");
            showAlertModal(this.$store, this.$t("reservations.scheduleUpdated"), "success", 4000);
        },
        handleReservationScheduleError(errorCode) {
            const schedulePatchErrorKeys = {
                change_reason_required: "reservations.errors.changeReasonRequired",
                invalid_schedule_payload: "reservations.errors.invalidSchedulePayload",
                invalid_schedule_datetime: "reservations.errors.invalidScheduleDatetime",
                invalid_schedule_dates: "reservations.errors.invalidScheduleDates",
                schedule_outside_entry_loading_window:
                    "reservations.errors.scheduleOutsideEntryLoadingWindow",
                schedule_outside_entry_unloading_window:
                    "reservations.errors.scheduleOutsideEntryUnloadingWindow",
                schedule_times_outside_entry_loading_window:
                    "reservations.errors.scheduleTimesOutsideEntryLoadingWindow",
                schedule_times_outside_entry_unloading_window:
                    "reservations.errors.scheduleTimesOutsideEntryUnloadingWindow",
                express_uniqueness_conflict: "reservations.expressUniquenessError",
            };
            const translationKey = schedulePatchErrorKeys[errorCode];
            showAlertModal(
                this.$store,
                translationKey ? this.$t(translationKey) : errorCode || this.$t("common.errorTryLater"),
                "danger",
                7000
            );
        },
        async fetchReservations() {
            this.loading = true;
            try {
                if (this.activeTab === "contracted") {
                    await markReservationTabViewed(this.entryType, this.activeTab);
                }
                const [contracted, received] = await Promise.all([
                    axios.get(`${process.env.VUE_APP_API_URL}/reservations/?my=contracted&entry_type=${this.entryType}`),
                    axios.get(`${process.env.VUE_APP_API_URL}/reservations/?my=received&entry_type=${this.entryType}`),
                ]);
                this.contractedReservations = contracted.data;
                this.receivedReservations = received.data;
                emitter.emit("refresh-reservation-badge");
            } catch (error) {
                console.error("Failed to fetch reservations:", error);
                showAlertModal(this.$store, "Failed to load reservations", "danger", 5000);
            } finally {
                this.loading = false;
            }
        },
        async handleConfirm(reservation, driverInfo) {
            try {
                await axios.post(
                    `${process.env.VUE_APP_API_URL}/reservations/${reservation.uid}/confirm/`,
                    driverInfo && typeof driverInfo === "object" ? driverInfo : {}
                );
                await this.fetchReservations();
                emitter.emit("refresh-reservation-badge");
            } catch (error) {
                const errorCode = error.response?.data?.error;
                if (errorCode === "counter_offer_pending") {
                    showAlertModal(this.$store, this.$t("reservations.errors.counterOfferPending"), "danger", 6000);
                } else {
                    showAlertModal(this.$store, errorCode || "Failed to confirm", "danger", 5000);
                }
            }
        },
        async handlePriceOffer(reservation, payload) {
            try {
                const body = {
                    price_offer: payload.price_offer,
                    ...(payload.price_offer_currency
                        ? { price_offer_currency: payload.price_offer_currency }
                        : {}),
                    ...(typeof payload.price_offer_vat_type === "boolean"
                        ? { price_offer_vat_type: payload.price_offer_vat_type }
                        : {}),
                };
                await axios.post(`${process.env.VUE_APP_API_URL}/reservations/${reservation.uid}/price-offer/`, body);
                await this.fetchReservations();
                emitter.emit("refresh-reservation-badge");
                showAlertModal(this.$store, this.$t("reservations.newPriceOfferSent"), "success", 4000);
            } catch (error) {
                const errorCode = error.response?.data?.error;
                const errorCodeToTranslationKey = {
                    offer_limit_reached: "reservations.errors.offerLimitReached",
                    offer_not_allowed_yet: "reservations.errors.offerNotAllowedYet",
                    price_offer_not_applicable: "reservations.errors.priceOfferNotApplicable",
                    price_offer_currency_required: "reservations.errors.priceOfferCurrencyRequired",
                    price_offer_vat_type_required: "reservations.errors.priceOfferVatTypeRequired",
                    invalid_price_offer: "reservations.errors.invalidPriceOffer",
                };
                const translationKey = errorCodeToTranslationKey[errorCode];
                showAlertModal(
                    this.$store,
                    translationKey
                        ? this.$t(translationKey)
                        : error.response?.data?.error || this.$t("reservations.errors.priceOfferFailed"),
                    "danger",
                    6000
                );
            }
        },
        async handlePriceCounterOffer(reservation, payload) {
            try {
                const requestBody =
                    payload && typeof payload === "object"
                        ? {
                              price_counter_offer: payload.price_counter_offer,
                              ...(payload.driver_name
                                  ? {
                                        driver_name: payload.driver_name,
                                        driver_surname: payload.driver_surname,
                                        car_number: payload.car_number,
                                        customs: payload.customs,
                                    }
                                  : {}),
                          }
                        : { price_counter_offer: payload };
                await axios.post(
                    `${process.env.VUE_APP_API_URL}/reservations/${reservation.uid}/price-counter-offer/`,
                    requestBody
                );
                await this.fetchReservations();
                emitter.emit("refresh-reservation-badge");
                const entrySummary = reservation.entry_summary || {};
                const listed =
                    entrySummary.entry_price != null && entrySummary.entry_price !== "";
                const currencyCode = listed
                    ? entrySummary.entry_currency || ""
                    : reservation.last_offer_currency || reservation.price_offer_currency || "";
                const vatRaw = listed
                    ? entrySummary.entry_vat_type
                    : reservation.last_offer_vat_type ?? reservation.price_offer_vat_type;
                const vatLabel =
                    vatRaw === true
                        ? this.$t("formFields.withVAT")
                        : vatRaw === false
                          ? this.$t("formFields.withoutVAT")
                          : "";
                const counterOfferAmount =
                    payload && typeof payload === "object" ? payload.price_counter_offer : payload;
                const priceLabel = [counterOfferAmount, currencyCode, vatLabel].filter(Boolean).join(" ").trim();
                showAlertModal(
                    this.$store,
                    this.$t("reservations.counterOfferSentSuccess", {
                        price: priceLabel,
                        companyName: reservation.reserved_by_company?.name || "—",
                    }),
                    "success",
                    4000
                );
            } catch (error) {
                const errorCode = error.response?.data?.error;
                const errorCodeToTranslationKey = {
                    counter_offer_limit_reached: "reservations.errors.counterOfferLimitReached",
                    counter_offer_not_allowed_yet: "reservations.errors.counterOfferNotAllowedYet",
                    invalid_price_counter_offer: "reservations.errors.invalidPriceCounterOffer",
                };
                const translationKey = errorCodeToTranslationKey[errorCode];
                showAlertModal(
                    this.$store,
                    translationKey
                        ? this.$t(translationKey)
                        : error.response?.data?.error || this.$t("reservations.errors.counterOfferFailed"),
                    "danger",
                    5000
                );
            }
        },
        async handleAcceptPriceCounterOffer(reservation) {
            try {
                await axios.post(
                    `${process.env.VUE_APP_API_URL}/reservations/${reservation.uid}/accept-price-counter-offer/`
                );
                await this.fetchReservations();
                emitter.emit("refresh-reservation-badge");
                showAlertModal(this.$store, this.$t("reservations.acceptCounterOfferSuccess"), "success", 3000);
            } catch (error) {
                showAlertModal(
                    this.$store,
                    error.response?.data?.error || this.$t("reservations.errors.acceptCounterFailed"),
                    "danger",
                    5000
                );
            }
        },
        async handleDecline(reservation) {
            try {
                await axios.post(`${process.env.VUE_APP_API_URL}/reservations/${reservation.uid}/decline/`);
                await this.fetchReservations();
                emitter.emit("refresh-reservation-badge");
            } catch (error) {
                showAlertModal(this.$store, error.response?.data?.error || "Failed to decline", "danger", 5000);
            }
        },
        openCancelConfirm(reservation) {
            this.cancelReservationToConfirm = reservation;
        },
        closeCancelConfirm() {
            this.cancelReservationToConfirm = null;
        },
        async confirmCancelReservation() {
            const reservation = this.cancelReservationToConfirm;
            this.closeCancelConfirm();
            if (!reservation) return;
            try {
                await axios.post(`${process.env.VUE_APP_API_URL}/reservations/${reservation.uid}/cancel/`);
                await this.fetchReservations();
                emitter.emit("refresh-reservation-badge");
                showAlertModal(this.$store, this.$t("reservations.reservationCancelled"), "success", 3000);
            } catch (error) {
                showAlertModal(this.$store, error.response?.data?.error || "Failed to cancel", "danger", 5000);
            }
        },
        async handleGenerateTransportOrder(reservation) {
            try {
                const { data } = await axios.get(
                    `${process.env.VUE_APP_API_URL}/reservations/${reservation.uid}/transport-order/`,
                    { params: { lang: this.$i18n?.locale || "en" } }
                );
                this.transportOrderReservation = reservation;
                this.transportOrderFieldValues = data.field_values || {};
                this.transportOrderModalVisible = true;
            } catch (error) {
                showAlertModal(this.$store, error.response?.data?.error || "Failed to load transport order", "danger", 5000);
            }
        },
        async handleModifyTransportOrder(reservation) {
            await this.handleGenerateTransportOrder(reservation);
        },
        handleDownloadTransportOrder(reservation) {
            const url = reservation.transport_order_preview?.generated_pdf_url;
            if (url) window.open(url, "_blank");
        },
        async handleSendTransportOrder(reservation) {
            try {
                await axios.post(`${process.env.VUE_APP_API_URL}/reservations/${reservation.uid}/transport-order/send/`);
                const email = reservation.reserved_by_user?.email || "-";
                showAlertModal(this.$store, this.$t("reservations.transportOrderSentSuccessfullyTo", { email }), "success", 3000);
            } catch (error) {
                showAlertModal(this.$store, error.response?.data?.error || "Failed to send", "danger", 5000);
            }
        },
        async handleTransportOrderSave(payload) {
            if (!this.transportOrderReservation) return;
            const reservation = this.transportOrderReservation;
            let displayTimezone;
            try {
                displayTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            } catch {
                displayTimezone = undefined;
            }
            const body = {
                transport_order_number: payload.transport_order_number,
                driver_name: payload.driver_name,
                driver_surname: payload.driver_surname,
                car_number: payload.car_number,
                customs: payload.customs ?? "",
                notes: payload.observations || "",
                weight: payload.weight,
                volume: payload.volume,
                mdp: payload.mdp,
                price: payload.price,
                currency: payload.currency,
                vat_type: payload.vat_type,
                payment_term_code: payload.payment_term_code,
                distance_km: payload.distance_km,
                duration_text: payload.duration_text,
                pickup_address: payload.pickup_address || {},
                delivery_address: payload.delivery_address || {},
                display_timezone: displayTimezone,
                truck_special_features: payload.truck_special_features || [],
            };
            if (this.entryType === "ware") {
                body.platform_type = payload.platform_type || [];
            }
            if (this.entryType === "truck") {
                body.truck_type = payload.truck_type;
            }
            body.transport_order_loading_date_iso = payload.transport_order_loading_date_iso;
            body.transport_order_unloading_date_iso = payload.transport_order_unloading_date_iso;
            body.loading_time_start = payload.loading_time_start;
            body.loading_time_end = payload.loading_time_end;
            body.unloading_time_start = payload.unloading_time_start;
            body.unloading_time_end = payload.unloading_time_end;
            body.transport_order_schedule_notes = payload.transport_order_schedule_notes ?? "";

            try {
                await axios.post(`${process.env.VUE_APP_API_URL}/reservations/${reservation.uid}/transport-order/`, body);
                this.transportOrderModalVisible = false;
                this.transportOrderReservation = null;
                this.transportOrderFieldValues = null;
                await this.fetchReservations();
                showAlertModal(this.$store, "Transport order saved.", "success", 3000);
            } catch (error) {
                const errorCode = error.response?.data?.error;
                const transportOrderErrorKeyMap = {
                    transport_order_schedule_dates_incomplete: "reservations.errors.transportOrderScheduleDatesIncomplete",
                    invalid_transport_order_schedule_dates: "reservations.errors.invalidTransportOrderScheduleDates",
                    reservation_schedule_anchors_missing: "reservations.errors.reservationScheduleAnchorsMissing",
                    transport_order_loading_date_outside_tolerance:
                        "reservations.errors.transportOrderLoadingDateOutsideTolerance",
                    transport_order_unloading_date_outside_tolerance:
                        "reservations.errors.transportOrderUnloadingDateOutsideTolerance",
                    transport_order_driver_fields_required:
                        "reservations.errors.transportOrderDriverFieldsRequired",
                };
                const translationKey = transportOrderErrorKeyMap[errorCode];
                showAlertModal(
                    this.$store,
                    translationKey ? this.$t(translationKey) : errorCode || "Failed to save transport order",
                    "danger",
                    6000
                );
            }
        },
        async handleConfirmTransportOrder(reservation) {
            try {
                await axios.post(`${process.env.VUE_APP_API_URL}/reservations/${reservation.uid}/transport-order/confirm/`);
                await this.fetchReservations();
                showAlertModal(this.$store, "Transport order confirmed.", "success", 3000);
            } catch (error) {
                showAlertModal(this.$store, error.response?.data?.error || "Failed to confirm", "danger", 5000);
            }
        },
        async handleUploadSignedPdf(reservation, file) {
            const formData = new FormData();
            formData.append("signed_pdf", file);
            try {
                await axios.post(`${process.env.VUE_APP_API_URL}/reservations/${reservation.uid}/transport-order/upload-signed/`, formData, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
                await this.fetchReservations();
                showAlertModal(this.$store, "Signed PDF uploaded.", "success", 3000);
            } catch (error) {
                showAlertModal(this.$store, error.response?.data?.error || "Failed to upload", "danger", 5000);
            }
        },
        async handleUploadCmr(reservation, file) {
            const formData = new FormData();
            formData.append("cmr_pdf", file);
            try {
                await axios.post(`${process.env.VUE_APP_API_URL}/reservations/${reservation.uid}/transport-order/upload-cmr/`, formData, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
                await this.fetchReservations();
                showAlertModal(this.$store, this.$t("reservations.cmrUploaded"), "success", 3000);
            } catch (error) {
                showAlertModal(this.$store, error.response?.data?.error || "Failed to upload", "danger", 5000);
            }
        },
        async handleUploadInvoice(reservation, file) {
            const formData = new FormData();
            formData.append("invoice_pdf", file);
            try {
                await axios.post(`${process.env.VUE_APP_API_URL}/reservations/${reservation.uid}/transport-order/upload-invoice/`, formData, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
                await this.fetchReservations();
                showAlertModal(this.$store, this.$t("reservations.invoiceUploaded"), "success", 3000);
            } catch (error) {
                showAlertModal(this.$store, error.response?.data?.error || "Failed to upload", "danger", 5000);
            }
        },
        async handleUploadPaymentProof(reservation, file) {
            const formData = new FormData();
            formData.append("payment_proof", file);
            try {
                await axios.post(`${process.env.VUE_APP_API_URL}/reservations/${reservation.uid}/transport-order/upload-payment-proof/`, formData, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
                await this.fetchReservations();
                showAlertModal(this.$store, this.$t("reservations.paymentProofUploaded"), "success", 3000);
            } catch (error) {
                showAlertModal(this.$store, error.response?.data?.error || "Failed to upload", "danger", 5000);
            }
        },
        async revealLinkedReservation(uid) {
            const isReceived = this.activeTab === "received";
            const raw = isReceived ? this.receivedReservations : this.contractedReservations;
            if (!(raw || []).some((reservation) => String(reservation.uid) === String(uid))) return;
            const filtered = () =>
                isReceived ? this.filteredReceivedReservations : this.filteredContractedReservations;
            let index = filtered().findIndex((reservation) => String(reservation.uid) === String(uid));
            if (index < 0) {
                const blank = {
                    loadingLocation: { country_code: null, place_id: null, place_display_name: null },
                    unloadingLocation: { country_code: null, place_id: null, place_display_name: null },
                    identificationId: null,
                    transportOrderNumber: null,
                    statuses: null,
                    missingDocuments: false,
                    inNegotiation: false,
                };
                if (isReceived) this.receivedFilters = blank;
                else this.contractedFilters = blank;
                await this.$nextTick();
                index = filtered().findIndex((reservation) => String(reservation.uid) === String(uid));
            }
            if (index < 0) return;
            const page = Math.floor(index / this.pageSize) + 1;
            if (isReceived) this.receivedPage = page;
            else this.contractedPage = page;
            await this.$nextTick();
            document.getElementById(`reservation-card-${uid}`)?.scrollIntoView({
                behavior: "smooth",
                block: "center",
            });
        },
        async handleUploadGeneratedTransportOrder(reservation, file) {
            const formData = new FormData();
            formData.append("generated_pdf", file);
            try {
                await axios.post(
                    `${process.env.VUE_APP_API_URL}/reservations/${reservation.uid}/transport-order/upload-generated/`,
                    formData,
                    {
                        headers: { "Content-Type": "multipart/form-data" },
                    }
                );
                await this.fetchReservations();
                emitter.emit("refresh-reservation-badge");
                showAlertModal(this.$store, this.$t("reservations.transportOrderPdfUploaded"), "success", 3000);
            } catch (error) {
                showAlertModal(this.$store, error.response?.data?.error || "Failed to upload", "danger", 5000);
            }
        },
        async handleConfirmCompleted(reservation) {
            try {
                await axios.post(`${process.env.VUE_APP_API_URL}/reservations/${reservation.uid}/confirm-completed/`);
                await this.fetchReservations();
                showAlertModal(this.$store, "Completed confirmed.", "success", 3000);
            } catch (error) {
                showAlertModal(this.$store, error.response?.data?.error || "Failed to confirm", "danger", 5000);
            }
        },
        async handleRated(reservation) {
            await this.fetchReservations();
            const companyName =
                reservation.rated_company_id === reservation.reserved_by_company?.id
                    ? reservation.reserved_by_company?.name
                    : reservation.owner_company_name;
            const company = companyName || "—";
            showAlertModal(this.$store, this.$t("rating.submittedFor", { company }), "success", 3000);
        },
    },
};
</script>

<style scoped>
.reservations-view {
    min-height: 0;
}

.reservations-tabs-wrapper,
.reservations-list-scroll {
    min-height: 0;
}

.reservations-list-scroll {
    scrollbar-width: none;
}

.reservations-list-scroll::-webkit-scrollbar {
    width: 0;
    height: 0;
}

.reservations-pagination {
    border-top: thin solid rgba(0, 0, 0, 0.08);
    background: rgb(var(--v-theme-surface));
}

.reservation-info-content {
    max-height: 60vh;
    overflow-y: auto;
}

.reservation-process-video-wrap {
    width: 100%;
}

.reservation-process-video {
    display: block;
    width: 100%;
    max-height: min(40vh, 320px);
    border-radius: 8px;
    background: #000;
}

.reservation-process-flow-panels {
    --panel-divider: rgba(0, 0, 0, 0.12);
}

.reservation-process-flow-panels :deep(.v-expansion-panel) {
    border-block-end: thin solid var(--panel-divider);
}

.reservation-process-flow-panels :deep(.v-expansion-panel:last-child) {
    border-block-end: none;
}

.reservation-process-flow-panels :deep(.v-expansion-panel-title) {
    padding-inline: 0;
    min-height: 44px;
    font-weight: 700;
}

.reservation-process-flow-panels :deep(.v-expansion-panel-text__wrapper) {
    padding: 0 0 12px;
}
</style>
